import { getAvatarUrl } from "common/avatar"
import http, { isHttpError } from "common/http"
import companiesDb from "components/companies/companies.db"
import CompaniesDb from "components/companies/companies.db"
import { CompaniesSchemaWithId } from "components/companies/companies.schema"
import type { User as HubUser } from "components/hub/hub.schema"
import ConstraintsConfig from "components/planscan/constraints/constraints.config.json"
import PlanscanConfig from "components/planscan/planscan.config.json"
import ProjectConfig from "components/project/project.config.json"
import ProjectDb from "components/project/project.db"
import {
    AttachmentReference,
    CompaniesInProject,
    Holiday,
    Location,
    ProjectSchema,
    ProjectSchemaWithId,
    User as ProjectUser
} from "components/project/project.schema"
import { UsersSchemaWithId } from "components/users/users.schema"
import cloneDeep from "lodash/cloneDeep"
import compact from "lodash/compact"
import isEqual from "lodash/isEqual"
import uniq from "lodash/uniq"
import Vue from "vue"
import { Component } from "vue-property-decorator"

/**
 * Gets user project settings
 *
 * @param userId The user's id
 * @param project The project to get the settings from
 * @returns The user's settings within the project
 */
export function getUserProjectSettings(userId: number, project: ProjectSchema): ProjectUser | undefined {
    return project.data.companies_in_project.reduce((projectUser, company) => {
        if (!projectUser) projectUser = company.users.find((user) => user.id === userId)

        return projectUser
    }, undefined as ProjectUser | undefined)
}

/**
 * Counts admins in project
 *
 * @param companies The companies array within the project
 * @returns The number of admins in project
 */
export function countAdminsInProject(companies: CompaniesInProject[]): number {
    return companies.reduce((numberOfAdmins, company) => {
        numberOfAdmins += company.users.reduce((numberOfAdmins, user) => {
            if (user.roles.some((role) => role.toLowerCase() === "admin") && user.active && company.active) numberOfAdmins++

            return numberOfAdmins
        }, 0)

        return numberOfAdmins
    }, 0)
}

/**
 * Gets user project company
 *
 * @param userId The user's id
 * @param project The project to get the user's company from
 * @returns The company the user is a part of in the project or undefined
 */
export function getUserProjectCompany(userId: number, project: ProjectSchema): CompaniesInProject | undefined {
    return project.data.companies_in_project.find((company) => company.users.some((user) => user.id === userId))
}

/**
 * Determines whether user is in a project
 *
 * @param userId The id of the user you want to check
 * @param project The project to check
 * @returns True if the user is in the the project and is active
 */
export function isUserInProject(userId: number, project: ProjectSchema): boolean {
    for (const company of project.data.companies_in_project) if (company.users.some((x) => x.id === userId && x.active === true)) return true

    return false
}

/**
 *
 * @param email
 */
export function isUserFromAllowedDomain(email: string): boolean {
    const allowedDomains = ["@bartonmalow.com", "@bmco.com", "@ncircletech.com"]
    const whitelist = ["parham38@gmail.com", "parham38@yahoo.com"]
    return !!email && allowedDomains.some((domain) => email.endsWith(domain) || whitelist.includes(email))
}

export const DEFAULT_ENTITY_NAME = "Barton Malow"
export const SUPERINTENDENT_DISCIPLINE = "Superintendent"

/**
 *
 * @param project project to validate
 * @returns string | ProjectSchema
 */
export function validateProject(project: ProjectSchema): string | ProjectSchema {
    project.data.name = project.data.name.trim()
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!project.data.can_create_shifts) project.data.can_create_shifts = ["admin"]
    if (!project.data.can_create_shifts.includes("admin")) project.data.can_create_shifts.unshift("admin")

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!project.data.can_edit_areas) project.data.can_edit_areas = ["admin"]
    if (!project.data.can_edit_areas.includes("admin")) project.data.can_edit_areas.unshift("admin")

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!project.data.can_add_company) project.data.can_add_company = ["admin"]
    if (!project.data.can_add_company.includes("admin")) project.data.can_add_company.unshift("admin")

    if (project.data.name === "") return "Project name cannot be blank"
    if (project.data.location.lat === -1 || project.data.location.long === -1) return "Please enter a valid project address."

    if (project.data.companies_in_project.length === 0) return "There must be at least one company in order to create a project."

    if (countAdminsInProject(project.data.companies_in_project) < 1) return "There must be at least one project admin in order to create a project."

    for (const company of project.data.companies_in_project) {
        for (const user of company.users)
            if (company.users.filter((x) => x.id === user.id).length > 1) return "Duplicate user entry in project company."
    }

    const userIds = project.data.companies_in_project.reduce((activeUserIds, company) => {
        const activeCompanyUserIds = company.users.filter((x) => x.active === true).map((x) => x.id)
        activeUserIds.push(...activeCompanyUserIds)
        return activeUserIds
    }, [] as number[])

    if (compact(userIds).length !== userIds.length) return "A user cannot be in two companies in the same project."

    return project
}

/**
 * Get a Project's name from Id
 *
 * @param projectId The project id
 * @param projects The list of projects to search
 * @returns The project's name or "Unknown Project"
 */
export function projectName(projectId: number, projects: ProjectSchema[]): string {
    const projectRecord = projects.find((projectRecord) => projectRecord.id === projectId)
    return projectRecord ? projectRecord.data.name : "Unknown Project"
}

/**
 * The project address
 *
 * @param projectId current Project id
 * @param projects All projects
 * @returns Address for project
 */
export function projectAddress(projectId: number, projects: ProjectSchema[]): string {
    const projectRecord = projects.find((projectRecord) => projectRecord.id === projectId)
    return projectRecord ? projectRecord.data.location.address : "Unknown Address"
}

/**
 * Colors luminance
 *
 * @param hex of color
 * @param lum dark/light
 * @returns luminance
 */
export function colorLuminance(hex: string, lum: number): string {
    // validate hex string
    if (!hex) return "000000"
    hex = String(hex).replace(/[^0-9a-f]/gi, "")
    if (hex.length < 6) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]

    // convert to decimal and change luminosity
    let rgb = "#",
        c,
        i

    for (i = 0; i < 3; i++) {
        c = parseInt(hex.substr(i * 2, 2), 16)
        c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16)
        rgb += ("00" + c).substr(c.length)
    }

    return rgb
}

/**
 * Get project logo or default logo with project name string
 *
 * @param projectId project id to get logo for
 * @param sessionId User session id
 * @param base64 if true returns base64 string
 * @returns string logo
 */
export async function getProjectLogo(
    projectId: number,
    sessionId?: string,
    base64?: boolean
): Promise<{
    url: string
    isSvg: boolean
}> {
    const project = await ProjectDb.read(projectId, sessionId)
    if (isHttpError(project)) {
        return {
            url: "",
            isSvg: false
        }
    }

    const logos: AttachmentReference[] = project[0].data.attachments.filter((attachment) => attachment.tags.indexOf("logo") !== -1)

    if (logos.length > 0) {
        const attachmentStreamUrl = `${process.env.FLYPAPER_HOST}/attachments/thumbnail/${logos[0].id}`
        return {
            url: attachmentStreamUrl,
            isSvg: false
        }
    } else {
        const url = getAvatarUrl(project[0].data.name, undefined)
        let avtarurl = await http.get<string>(url, {
            headers: {
                Authorization: "Bearer " + sessionId
            }
        })

        if (isHttpError(avtarurl)) avtarurl = ""

        return {
            url: avtarurl,
            isSvg: !base64
        }
    }
}

/**
 * Get project logo or default logo with project name string
 *
 * @param projectId project id to get logo for
 * @param projects All projects
 * @returns string logo
 */
export function getProjectLogoSync(projectId: number, projects: ProjectSchemaWithId[]): string {
    const project = projects.find((x) => x.id === projectId)
    if (!project) return ""

    const logos: AttachmentReference[] = project.data.attachments.filter((attachment) => attachment.tags.indexOf("logo") !== -1)

    if (logos.length > 0) {
        const attachmentStreamUrl = `${process.env.FLYPAPER_HOST}/attachments/thumbnail/${logos[0].id}`
        return attachmentStreamUrl
    } else {
        let projectName = project.data.name
        projectName = projectName.split(" ").join("+")
        return `https://ui-avatars.com/api/?name=${projectName}`
    }
}

/**
 * @param project project data
 * @param user user data
 * @returns if user is project team member or admin
 */
export function isProjectTeamMemberOrAdmin(project: ProjectSchemaWithId, user: UsersSchemaWithId): boolean {
    for (const companyInProject of project.data.companies_in_project) {
        if (companyInProject.active) {
            for (const companyUser of companyInProject.users) {
                if (companyUser.active && companyUser.id === user.id)
                    if (companyUser.roles.includes("admin") || companyUser.roles.includes("team member")) return true
            }
        }
    }

    return false
}

/**
 * Get all admin users email
 *
 * @param project Project for which to get admin users for
 * @param allUsers All users
 * @returns string[] | Array of admin user emails
 */
export function getAllAdminUsers(project: ProjectSchemaWithId, allUsers: UsersSchemaWithId[]): string[] {
    const adminEmails: string[] = []
    const projectAdminIds = getAllAdminUserIds(project)

    projectAdminIds.forEach((userId) => {
        const adminUsersToPush = allUsers.find((user) => {
            return userId === user.id
        })

        if (adminUsersToPush) adminEmails.push(adminUsersToPush.data.email)
    })

    return uniq(adminEmails)
}

/**
 * Get all admin users email
 *
 * @param project Project for which to get admin users for
 * @returns string[] | Array of admin user emails
 */
export function getAllAdminUserIds(project: ProjectSchemaWithId): number[] {
    const adminUserIds: number[] = []

    for (const companyInProject of project.data.companies_in_project) {
        if (companyInProject.active) {
            for (const companyUser of companyInProject.users)
                if (companyUser.roles.includes("admin") && companyUser.active) adminUserIds.push(companyUser.id)
        }
    }

    return uniq(adminUserIds)
}

/**
 *
 * @param projectName project Name
 * @param projectCode projectCode
 * @param location location
 * @param enablePlanScan
 * @returns ProjectSchema as blank project
 */
export function createBlankProject(
    projectName: string,
    projectCode?: string | undefined,
    location?: Location | undefined,
    enablePlanScan?: boolean
): ProjectSchema {
    const blankProject: ProjectSchema = {
        date_created: "",
        date_modified: "",
        created_by: 0,
        modified_by: 0,
        data: {
            name: projectName,
            active: true,
            code: projectCode || undefined,
            is_joint_venture: false,
            areas: [],
            distribution_lists: [],
            attachments: [],
            connections: {},
            location: location || ProjectConfig.defaults.location,
            shift_ids: [],
            survey_templates: [],
            workdays: ProjectConfig.weekdays_with_indexes.map((x) => x.day),
            daily: {
                settings: {
                    active: true,
                    automatically_create: false,
                    lock_daily_after: 30
                },
                daily_report_grouping: "Tags"
            },
            planscan: {
                schedule: -1,
                settings: {
                    can_update_dates: PlanscanConfig.defaults.can_update_dates,
                    active: enablePlanScan ?? false,
                    // TODO this needs to come from the hub not the config file.
                    can_complete_activities: PlanscanConfig.defaults.can_complete_activities,
                    // TODO this needs to come from the hub not the config file.
                    constraint_types: ConstraintsConfig.default_constraint_types,
                    ppc: {
                        // TODO this needs to come from the hub not the config file
                        calculation_method: ProjectConfig.ppcCalculationMethods[0],
                        constraint_types: []
                    }
                }
            },
            can_invite_user: ProjectConfig.roles.map((role) => {
                return role.name
            }),
            can_add_company: ProjectConfig.defaultAuthorisedRolesToAddCompany,
            can_create_shifts: ProjectConfig.defaultAuthorisedRoles,
            can_edit_areas: ProjectConfig.defaultAuthorisedRoles,
            daily_ids: [],
            quantity_ids: [],
            quantities: {
                settings: {
                    active: false
                }
            },
            companies_in_project: []
        }
    }

    return blankProject
}

/**
 *
 * @param companyName
 * @param allCompanies
 */
export function getCompanyByName(companyName: string, allCompanies: CompaniesSchemaWithId[]) {
    // TODO this should be done on the server where allCompanies can be checked properly
    const existingCompany = allCompanies.find((company) => {
        return company.data.name.toLowerCase() === companyName.toLowerCase()
    })

    return existingCompany
}

/**
 *
 * @param project
 * @param companyId
 * @param sessionId
 */
export async function addCompanyToProjectRecord(project: ProjectSchema, companyId: number, sessionId?: string) {
    project = cloneDeep(project)
    const companyInProjectIndex = project.data.companies_in_project.findIndex((company) => company.company_id === companyId)

    const companyResponse = await companiesDb.read(companyId, sessionId)
    if (isHttpError(companyResponse)) return companyResponse

    const company = companyResponse[0]

    if (companyInProjectIndex >= 0 && project.data.companies_in_project[companyInProjectIndex].active === false) {
        // NOTE reactivate the company but make sure all the users are marked inactive we don't want users to be reactivated by accident
        project.data.companies_in_project[companyInProjectIndex].active = true
        project.data.companies_in_project[companyInProjectIndex].users = project.data.companies_in_project[companyInProjectIndex].users.map(
            (user) => {
                user.active = false
                return user
            }
        )
    } else if (companyInProjectIndex < 0) {
        project.data.companies_in_project.push({
            company_id: companyId,
            active: true,
            users: [],
            color: company.data.color,
            project_logo: company.data.attachments.find((attachment) => attachment.tags.includes("logo"))
        })
    }

    return project
}

/**
 *
 * @param holiday
 * @param index
 * @param holidays
 * @param onDuplicate
 * @param onModified
 * @param showDuplicateError
 * @param markModified
 */
export function updateHolidayCommon(
    holiday: Holiday,
    index: number,
    holidays: Holiday[] | undefined,
    showDuplicateError: () => void,
    markModified: () => void
) {
    if (!holidays || index < 0 || index >= holidays.length) return

    const isDuplicate = holidays.some((h, i) => i !== index && h.name.trim().toLowerCase() === holiday.name.trim().toLowerCase())

    if (isDuplicate) {
        showDuplicateError()
        holidays.splice(index, 1, { ...holidays[index] })
        return
    }

    holidays.splice(index, 1, holiday)

    markModified()
}

/**
 * Normalizes a daily report send time into a zero-padded HH:MM:SS string.
 *
 * @param value A time string with 1-3 colon-separated segments, e.g. "9:5" or "09:05:00"
 * @returns The value with missing segments defaulted to "00" and every segment zero-padded to 2 digits
 */
export function normalizeDailyReportSendTime(value: string): string {
    const segments = value.split(":")
    while (segments.length < 3) segments.push("00")
    return segments.map((segment) => segment.padStart(2, "0")).join(":")
}

/**
 *
 * @param companyNameToAdd companyNameToAdd
 * @param user user schema
 * @param allCompanies allCompanies
 * @param hubToAddCompanyTo
 * @param projectToAddCompanyTo
 */
export async function createCompanyAndLinkItToHub(
    companyNameToAdd: string,
    user: UsersSchemaWithId,
    allCompanies: CompaniesSchemaWithId[],
    hubToAddCompanyTo: number | undefined,
    projectToAddCompanyTo: number | undefined
): Promise<CompaniesSchemaWithId | undefined> {
    if (projectToAddCompanyTo && !hubToAddCompanyTo) return
    const existingCompany = getCompanyByName(companyNameToAdd, allCompanies)
    const companyToAdd = existingCompany ?? (await CompaniesDb.createEmptyCompany(companyNameToAdd, user.id, hubToAddCompanyTo))
    if (isHttpError(companyToAdd)) return
    if (projectToAddCompanyTo) {
        const projectUpdate = await ProjectDb.addCompanyToProject(companyToAdd.id, projectToAddCompanyTo)
        if (isHttpError(projectUpdate)) return
        return companyToAdd
    } else {
        return companyToAdd
    }
}

/**
 * Maps each user with project access (active user in an active company) to their company and roles.
 *
 * @param companies companies_in_project
 * @returns active members keyed by user id
 */
export function getActiveProjectMembers(companies: CompaniesInProject[] = []): Map<number, { companyId: number; roles: string[] }> {
    const members = new Map<number, { companyId: number; roles: string[] }>()
    for (const company of companies) {
        if (!company.active) continue
        for (const user of company.users) if (user.active) members.set(user.id, { companyId: company.company_id, roles: user.roles })
    }

    return members
}

/**
 * Lists users who gain project access in newCompanies compared to oldCompanies.
 *
 * @param oldCompanies companies_in_project before the change
 * @param newCompanies companies_in_project after the change
 * @returns ids of users newly active in the project
 */
export function getNewlyActiveProjectUserIds(oldCompanies: CompaniesInProject[] = [], newCompanies: CompaniesInProject[] = []): number[] {
    const oldMembers = getActiveProjectMembers(oldCompanies)
    return [...getActiveProjectMembers(newCompanies).keys()].filter((userId) => !oldMembers.has(userId))
}

/**
 * Finds users a whole-project save would re-add although a hub admin removed them. A genuine re-add reactivates the
 * user in the hub before the project is saved, so a user who becomes active in the project while still inactive in
 * the hub can only come from an outdated copy (APEX-1527). The machine user is never blocked, because removing a
 * project's last admin backfills it as admin.
 *
 * @param storedCompanies companies_in_project as currently stored
 * @param incomingCompanies companies_in_project being saved
 * @param hubUsers The project hub's users.
 * @param [machineUserId] The machine user's id.
 * @returns ids of users whose re-add should be ignored
 */
export function getHubRemovedUserIdsToBlock(
    storedCompanies: CompaniesInProject[],
    incomingCompanies: CompaniesInProject[],
    hubUsers: HubUser[],
    machineUserId?: number
): number[] {
    return getNewlyActiveProjectUserIds(storedCompanies, incomingCompanies).filter(
        (userId) => userId !== machineUserId && hubUsers.some((hubUser) => hubUser.id === userId && !hubUser.active)
    )
}

/**
 * Deactivates every active entry of a user, including ones in a removed company. A leftover active entry in a second
 * company would otherwise keep the user's access, or restore it if that company is re-added.
 *
 * @param companies companies_in_project; mutated in place
 * @param userId The user to deactivate.
 * @param [removedBy] Who removed the user, recorded for History.
 * @returns the companies the user was deactivated in, those with project access first
 */
export function deactivateUserInAllCompanies(companies: CompaniesInProject[], userId: number, removedBy?: number): CompaniesInProject[] {
    const deactivatedIn: CompaniesInProject[] = []
    for (const company of companies) {
        let deactivated = false
        for (const user of company.users) {
            if (user.id !== userId || !user.active) continue

            user.active = false
            if (removedBy) user.metadata = { ...user.metadata, removed_by: removedBy }
            deactivated = true
        }

        if (deactivated) deactivatedIn.push(company)
    }

    return [...deactivatedIn.filter((company) => company.active), ...deactivatedIn.filter((company) => !company.active)]
}

/**
 * Removes a user's entries from every company except one, so they belong to a single company in the project.
 *
 * @param companies companies_in_project; mutated in place
 * @param userId The user.
 * @param keepCompanyId The company the user stays in.
 */
export function removeUserFromOtherCompanies(companies: CompaniesInProject[], userId: number, keepCompanyId: number): void {
    for (const company of companies) if (company.company_id !== keepCompanyId) company.users = company.users.filter((user) => user.id !== userId)
}

/**
 * Records the saving user as the inviter of every pending invite (active, invite not yet sent) the save adds or
 * changes, so a client can't name someone else as invited_by. The invite listener trusts invited_by to decide
 * whether a deleted user may be reactivated (super admin inviters only, APEX-1527). Pending invites carried over
 * unchanged from storedCompanies keep their inviter.
 *
 * @param storedCompanies companies_in_project as currently stored; empty for a new project
 * @param incomingCompanies companies_in_project being saved; mutated in place
 * @param inviterId The user saving the project.
 */
export function stampPendingInviters(storedCompanies: CompaniesInProject[], incomingCompanies: CompaniesInProject[], inviterId: number): void {
    for (const company of incomingCompanies) {
        const storedCompany = storedCompanies.find((stored) => stored.company_id === company.company_id)
        for (const user of company.users) {
            if (!user.active || user.metadata.invite_sent) continue

            const storedUser = storedCompany?.users.find((stored) => stored.id === user.id)
            const isUnchangedPendingInvite =
                storedUser?.active && !storedUser.metadata.invite_sent && storedUser.metadata.invited_by === user.metadata.invited_by

            if (!isUnchangedPendingInvite) user.metadata = { ...user.metadata, invited_by: inviterId }
        }
    }
}

export type ProjectMembershipChange =
    | { type: "company_added" | "company_removed" | "company_settings_changed"; companyId: number }
    | { type: "user_added"; userId: number; companyId: number; invitedBy?: number }
    | { type: "user_removed"; userId: number; companyId: number; removedBy?: number }
    | { type: "user_moved"; userId: number; fromCompanyId: number; toCompanyId: number }
    | { type: "user_roles_changed"; userId: number; companyId: number; oldRoles: string[]; newRoles: string[] }

/**
 * Lists who gained, lost or changed project access between two versions of companies_in_project.
 * A user has access when they are active in an active company. Users gaining or losing access only because
 * their whole company was added or removed are covered by the company change.
 *
 * @param oldCompanies companies_in_project before the change
 * @param newCompanies companies_in_project after the change
 * @returns the membership changes
 */
export function diffProjectMembership(oldCompanies: CompaniesInProject[] = [], newCompanies: CompaniesInProject[] = []): ProjectMembershipChange[] {
    const changes: ProjectMembershipChange[] = []
    const activeCompanyIds = (companies: CompaniesInProject[]) => new Set(companies.filter((company) => company.active).map((c) => c.company_id))
    const oldActiveCompanyIds = activeCompanyIds(oldCompanies)
    const newActiveCompanyIds = activeCompanyIds(newCompanies)

    for (const companyId of newActiveCompanyIds) if (!oldActiveCompanyIds.has(companyId)) changes.push({ type: "company_added", companyId })
    for (const companyId of oldActiveCompanyIds) if (!newActiveCompanyIds.has(companyId)) changes.push({ type: "company_removed", companyId })

    const settingsOf = (company: CompaniesInProject) => [company.abbreviation, company.color, company.disciplines, company.project_logo]
    for (const newCompany of newCompanies) {
        const oldCompany = oldCompanies.find((company) => company.company_id === newCompany.company_id)
        if (oldCompany && oldCompany.active && newCompany.active && !isEqual(settingsOf(oldCompany), settingsOf(newCompany)))
            changes.push({ type: "company_settings_changed", companyId: newCompany.company_id })
    }

    const oldMembers = getActiveProjectMembers(oldCompanies)
    const newMembers = getActiveProjectMembers(newCompanies)

    const findUser = (companies: CompaniesInProject[], companyId: number, userId: number) =>
        companies.find((company) => company.company_id === companyId)?.users.find((user) => user.id === userId)

    for (const [userId, newMember] of newMembers) {
        const oldMember = oldMembers.get(userId)
        if (!oldMember) {
            if (oldActiveCompanyIds.has(newMember.companyId)) {
                const invitedBy = findUser(newCompanies, newMember.companyId, userId)?.metadata.invited_by
                changes.push({ type: "user_added", userId, companyId: newMember.companyId, ...(invitedBy ? { invitedBy } : {}) })
            }
        } else if (oldMember.companyId !== newMember.companyId) {
            changes.push({ type: "user_moved", userId, fromCompanyId: oldMember.companyId, toCompanyId: newMember.companyId })
        } else if (!isEqual([...oldMember.roles].sort(), [...newMember.roles].sort())) {
            changes.push({ type: "user_roles_changed", userId, companyId: newMember.companyId, oldRoles: oldMember.roles, newRoles: newMember.roles })
        }
    }

    for (const [userId, oldMember] of oldMembers) {
        if (!newMembers.has(userId) && newActiveCompanyIds.has(oldMember.companyId)) {
            const removedBy = findUser(newCompanies, oldMember.companyId, userId)?.metadata.removed_by
            changes.push({ type: "user_removed", userId, companyId: oldMember.companyId, ...(removedBy ? { removedBy } : {}) })
        }
    }

    return changes
}

@Component
export class ProjectUtilsMixin extends Vue {
    public projectName(projectId: number, projects: ProjectSchema[]): string {
        return projectName(projectId, projects)
    }
    public projectAddress(projectId: number, projects: ProjectSchema[]): string {
        return projectAddress(projectId, projects)
    }

    public projectLogo(
        projectId: number,
        sessionId?: string,
        base64?: boolean
    ): Promise<{
        url: string
        isSvg: boolean
    }> {
        return getProjectLogo(projectId, sessionId, base64)
    }

    public projectLogoSync(projectId: number, projects: ProjectSchemaWithId[]): string {
        return getProjectLogoSync(projectId, projects)
    }
}
